'use client';

import { c as _c } from "react/compiler-runtime";
import { domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { Icon } from '@/components/ui/icon';
import { CheckmarkCircle02IconSolid, CircleIconSolid, HelpCircleIconSolid } from '@/components/ui/icon/glyphs';
import { Panel, PanelContent } from '@/components/ui/panel';
import { Link } from '@/i18n/navigation';
import { levelForDays, nextTier, tierForLevel } from '@/lib/streaks';
import { cn } from '@/lib/ui/utils';
import { TierEmblem } from './tier-emblem';
export interface StreakWidgetViewProps {
  currentStreak: number;
  longestStreak: number;
  todayComplete?: boolean | null;
  onViewCalendar: () => void;
  onViewLevels: () => void;
  onShowHelp?: () => void;
  helpOpen?: boolean;
  browseHref: string;
}
export function StreakWidgetView(t0) {
  const $ = _c(128);
  const {
    currentStreak,
    longestStreak,
    todayComplete: t1,
    onViewCalendar,
    onViewLevels,
    onShowHelp,
    helpOpen: t2,
    browseHref
  } = t0;
  const todayComplete = t1 === undefined ? null : t1;
  const helpOpen = t2 === undefined ? false : t2;
  const t = useTranslations("streaks");
  const reduce = useReducedMotion();
  let level;
  let t3;
  let tier;
  if ($[0] !== currentStreak) {
    level = levelForDays(currentStreak);
    tier = tierForLevel(level);
    t3 = nextTier(level);
    $[0] = currentStreak;
    $[1] = level;
    $[2] = t3;
    $[3] = tier;
  } else {
    level = $[1];
    t3 = $[2];
    tier = $[3];
  }
  const next = t3;
  const target = next?.minDays ?? tier?.minDays ?? 1;
  const progress = Math.min(1, currentStreak / target);
  let t4;
  if ($[4] !== t) {
    t4 = t("title");
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = onShowHelp === undefined;
  let t6;
  if ($[6] !== t) {
    t6 = t("how_it_works.title");
    $[6] = t;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Icon icon={HelpCircleIconSolid} className="size-icon-sm" aria-hidden={true} />;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== helpOpen || $[10] !== onShowHelp || $[11] !== t5 || $[12] !== t6) {
    t8 = <AnimatedButton variant="tertiary" size="xs" onClick={onShowHelp} disabled={t5} aria-label={t6} aria-haspopup="dialog" aria-expanded={helpOpen}>{t7}</AnimatedButton>;
    $[9] = helpOpen;
    $[10] = onShowHelp;
    $[11] = t5;
    $[12] = t6;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t4 || $[15] !== t8) {
    t9 = <div className="flex items-center gap-1 text-body-lg font-medium">{t4}{t8}</div>;
    $[14] = t4;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t) {
    t10 = t("view_levels");
    $[17] = t;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== onViewLevels || $[20] !== t10) {
    t11 = <AnimatedButton variant="tertiary" size="xs" onClick={onViewLevels}>{t10}</AnimatedButton>;
    $[19] = onViewLevels;
    $[20] = t10;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11 || $[23] !== t9) {
    t12 = <div className="flex items-center justify-between gap-2">{t9}{t11}</div>;
    $[22] = t11;
    $[23] = t9;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== currentStreak) {
    t13 = <AnimatedNumber value={currentStreak} format={_temp} className="text-display-xl leading-none tabular-nums" />;
    $[25] = currentStreak;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== currentStreak || $[28] !== t) {
    t14 = t("day_streak_label", {
      count: currentStreak
    });
    $[27] = currentStreak;
    $[28] = t;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t14) {
    t15 = <span className="text-body-lg font-normal text-foreground-secondary">{t14}</span>;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t13 || $[33] !== t15) {
    t16 = <div className="flex flex-wrap items-baseline gap-x-2">{t13}{t15}</div>;
    $[32] = t13;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t || $[36] !== tier) {
    t17 = tier ? t(`tiers.${tier.slug}`) : t("no_streak_yet");
    $[35] = t;
    $[36] = tier;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t17) {
    t18 = <span>{t17}</span>;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <span aria-hidden={true}>·</span>;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== longestStreak || $[42] !== t) {
    t20 = t("best_label", {
      count: longestStreak
    });
    $[41] = longestStreak;
    $[42] = t;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t20) {
    t21 = <span>{t20}</span>;
    $[44] = t20;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t18 || $[47] !== t21) {
    t22 = <div className="flex flex-wrap items-center gap-x-2 text-body font-normal text-foreground-secondary">{t18}{t19}{t21}</div>;
    $[46] = t18;
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== t16 || $[50] !== t22) {
    t23 = <div className="flex min-w-0 flex-col gap-1.5">{t16}{t22}</div>;
    $[49] = t16;
    $[50] = t22;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== level) {
    t24 = <TierEmblem level={level} size={112} className="size-24 shrink-0 sm:size-28" />;
    $[52] = level;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t23 || $[55] !== t24) {
    t25 = <div className="my-auto flex items-center justify-between gap-3">{t23}{t24}</div>;
    $[54] = t23;
    $[55] = t24;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== next || $[58] !== t) {
    t26 = next ? t("next_tier", {
      tier: t(`tiers.${next.slug}`)
    }) : t("eternal_unlocked");
    $[57] = next;
    $[58] = t;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== t26) {
    t27 = <span>{t26}</span>;
    $[60] = t26;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== currentStreak || $[63] !== next || $[64] !== t || $[65] !== target) {
    t28 = next ? t("days_away", {
      count: target - currentStreak
    }) : t("top_milestone");
    $[62] = currentStreak;
    $[63] = next;
    $[64] = t;
    $[65] = target;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  let t29;
  if ($[67] !== t28) {
    t29 = <span>{t28}</span>;
    $[67] = t28;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  let t30;
  if ($[69] !== t27 || $[70] !== t29) {
    t30 = <div className="flex flex-wrap items-center justify-between gap-2">{t27}{t29}</div>;
    $[69] = t27;
    $[70] = t29;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  let t31;
  if ($[72] !== next || $[73] !== t) {
    t31 = next ? t("next_tier", {
      tier: t(`tiers.${next.slug}`)
    }) : t("top_milestone");
    $[72] = next;
    $[73] = t;
    $[74] = t31;
  } else {
    t31 = $[74];
  }
  const t32 = Math.min(currentStreak, target);
  let t33;
  if ($[75] !== progress) {
    t33 = {
      scaleX: progress
    };
    $[75] = progress;
    $[76] = t33;
  } else {
    t33 = $[76];
  }
  let t34;
  if ($[77] !== reduce) {
    t34 = reduce ? {
      duration: 0
    } : {
      duration: 0.25,
      ease: "easeOut"
    };
    $[77] = reduce;
    $[78] = t34;
  } else {
    t34 = $[78];
  }
  let t35;
  if ($[79] !== t33 || $[80] !== t34) {
    t35 = <LazyMotion features={domAnimation}><m.span className="block h-full origin-left rounded-pill bg-funding-progress" initial={false} animate={t33} transition={t34} /></LazyMotion>;
    $[79] = t33;
    $[80] = t34;
    $[81] = t35;
  } else {
    t35 = $[81];
  }
  let t36;
  if ($[82] !== t31 || $[83] !== t32 || $[84] !== t35 || $[85] !== target) {
    t36 = <div role="progressbar" aria-label={t31} aria-valuemin={0} aria-valuemax={target} aria-valuenow={t32} className="h-1.5 overflow-hidden rounded-pill bg-border-primary">{t35}</div>;
    $[82] = t31;
    $[83] = t32;
    $[84] = t35;
    $[85] = target;
    $[86] = t36;
  } else {
    t36 = $[86];
  }
  const t37 = todayComplete === true && "text-feedback-success-content";
  let t38;
  if ($[87] !== t37) {
    t38 = cn("inline-flex items-center gap-1.5", t37);
    $[87] = t37;
    $[88] = t38;
  } else {
    t38 = $[88];
  }
  const t39 = todayComplete === true ? CheckmarkCircle02IconSolid : CircleIconSolid;
  let t40;
  if ($[89] !== t39) {
    t40 = <Icon icon={t39} className="size-icon-sm" aria-hidden={true} />;
    $[89] = t39;
    $[90] = t40;
  } else {
    t40 = $[90];
  }
  let t41;
  if ($[91] !== t || $[92] !== todayComplete) {
    t41 = todayComplete === null ? t("today_unknown") : todayComplete ? t("today_complete") : t("not_traded_today");
    $[91] = t;
    $[92] = todayComplete;
    $[93] = t41;
  } else {
    t41 = $[93];
  }
  let t42;
  if ($[94] !== t38 || $[95] !== t40 || $[96] !== t41) {
    t42 = <span className={t38}>{t40}{t41}</span>;
    $[94] = t38;
    $[95] = t40;
    $[96] = t41;
    $[97] = t42;
  } else {
    t42 = $[97];
  }
  let t43;
  if ($[98] !== next || $[99] !== t || $[100] !== target) {
    t43 = next ? t("unlocks_at", {
      count: target
    }) : t("final_milestone", {
      count: target
    });
    $[98] = next;
    $[99] = t;
    $[100] = target;
    $[101] = t43;
  } else {
    t43 = $[101];
  }
  let t44;
  if ($[102] !== t43) {
    t44 = <span>{t43}</span>;
    $[102] = t43;
    $[103] = t44;
  } else {
    t44 = $[103];
  }
  let t45;
  if ($[104] !== t42 || $[105] !== t44) {
    t45 = <div className="flex flex-wrap items-center justify-between gap-2">{t42}{t44}</div>;
    $[104] = t42;
    $[105] = t44;
    $[106] = t45;
  } else {
    t45 = $[106];
  }
  let t46;
  if ($[107] !== t30 || $[108] !== t36 || $[109] !== t45) {
    t46 = <div className="flex flex-col gap-3 rounded-panel-row bg-background-tertiary p-4 text-caption font-normal text-foreground-secondary">{t30}{t36}{t45}</div>;
    $[107] = t30;
    $[108] = t36;
    $[109] = t45;
    $[110] = t46;
  } else {
    t46 = $[110];
  }
  let t47;
  if ($[111] !== browseHref || $[112] !== currentStreak || $[113] !== t) {
    t47 = currentStreak === 0 ? <AnimatedButton variant="tertiary" size="xs" className="mr-auto" asChild={true}><Link href={browseHref}>{t("browse_markets")}</Link></AnimatedButton> : null;
    $[111] = browseHref;
    $[112] = currentStreak;
    $[113] = t;
    $[114] = t47;
  } else {
    t47 = $[114];
  }
  let t48;
  if ($[115] !== t) {
    t48 = t("view_activity");
    $[115] = t;
    $[116] = t48;
  } else {
    t48 = $[116];
  }
  let t49;
  if ($[117] !== onViewCalendar || $[118] !== t48) {
    t49 = <AnimatedButton variant="tertiary" size="xs" onClick={onViewCalendar} aria-haspopup="dialog">{t48}</AnimatedButton>;
    $[117] = onViewCalendar;
    $[118] = t48;
    $[119] = t49;
  } else {
    t49 = $[119];
  }
  let t50;
  if ($[120] !== t47 || $[121] !== t49) {
    t50 = <div className="flex flex-wrap items-center justify-end gap-2">{t47}{t49}</div>;
    $[120] = t47;
    $[121] = t49;
    $[122] = t50;
  } else {
    t50 = $[122];
  }
  let t51;
  if ($[123] !== t12 || $[124] !== t25 || $[125] !== t46 || $[126] !== t50) {
    t51 = <Panel className="h-full" data-testid="streak-widget"><PanelContent className="flex h-full flex-col gap-4">{t12}{t25}{t46}{t50}</PanelContent></Panel>;
    $[123] = t12;
    $[124] = t25;
    $[125] = t46;
    $[126] = t50;
    $[127] = t51;
  } else {
    t51 = $[127];
  }
  return t51;
}
function _temp(n) {
  return String(Math.floor(n));
}